const NO_ISSUE_VALUES = new Set(["none", "None", "NONE", ""]);

export const ISSUE_TYPE_LABELS = {
  missing: "Missing",
  damaged: "Damaged",
};

export const normalizeIssueType = (value) => {
  if (!value) return "";
  return String(value).trim().toLowerCase();
};

const isValidIssueSample = (issueSample) => {
  const issueType = normalizeIssueType(issueSample?.issue_type);
  return Boolean(issueType) && !NO_ISSUE_VALUES.has(issueType);
};

export const getSamplesWithIssues = (submission) => {
  const issues = Array.isArray(submission?.samples_with_issues)
    ? submission.samples_with_issues
    : [];
  return issues.filter(isValidIssueSample);
};

export const hasSampleIssues = (submission) => (
  getSamplesWithIssues(submission).length > 0
);

export const isMultipleSampleIssues = (submission) => (
  hasSampleIssues(submission) && getSamplesWithIssues(submission).length > 1
);

/** Submission has more than one sample (some may be OK, others with issues). */
export const hasMultipleSubmissionSamples = (submission) => {
  const numSamples = Number(submission?.num_samples);
  if (Number.isFinite(numSamples) && numSamples > 1) return true;
  const okCount = Array.isArray(submission?.samples_list) ? submission.samples_list.length : 0;
  return okCount > 0 && getSamplesWithIssues(submission).length > 0;
};

/** One missing/damaged sample while other samples in the submission are OK. */
export const isPartialSingleSampleIssue = (submission) => (
  getSamplesWithIssues(submission).length === 1 && hasMultipleSubmissionSamples(submission)
);

/** Parent row shows expand + count pill: 2+ issues, or 1 issue with other OK samples. */
export const isExpandableSampleIssues = (submission) => (
  isMultipleSampleIssues(submission) || isPartialSingleSampleIssue(submission)
);

export const ALL_SAMPLES_HAVE_ISSUES_TOOLTIP = "All the samples provided here in this submission have issues in it. So, please click on the expand icon or sample issue column to proceed further";

const normalizeTestList = (tests) => (
  (tests ?? []).map((test) => (typeof test === "string" ? test : test?.Test)).filter(Boolean)
);

/** Resolve tests from list (`tests_list`) or draft-style (`test_list`) sample shapes. */
export const getIssueSampleTests = (sample) => {
  if (!sample) return [];
  if (Array.isArray(sample.tests_list) && sample.tests_list.length) {
    return normalizeTestList(sample.tests_list);
  }
  return normalizeTestList(sample.test_list);
};

/** True when every sample in the submission has an issue (num_samples matches issue count). */
export const allSamplesHaveIssues = (submission) => {
  const issuesCount = getSamplesWithIssues(submission).length;
  if (!issuesCount) return false;

  const numSamples = Number(submission?.num_samples);
  if (Number.isFinite(numSamples) && numSamples > 0) {
    return numSamples === issuesCount;
  }

  const samplesList = submission?.samples_list;
  return Array.isArray(samplesList) && samplesList.length === 0;
};

export const getIssueDisplayLabel = (issueType) => (
  ISSUE_TYPE_LABELS[normalizeIssueType(issueType)] || issueType || "Issue"
);

export const getIssueSampleUniqueIdentifier = (issueSample) => (
  issueSample?.sample_unique_identifier
  ?? issueSample?.sample_identifier
  ?? issueSample?.sample_id
  ?? ""
);

export const findIssueSampleInList = (issueSample, samples) => {
  if (!issueSample || !Array.isArray(samples) || !samples.length) return null;

  const matchId = getIssueSampleUniqueIdentifier(issueSample);
  if (matchId) {
    const byUniqueId = samples.find((item) => (
      item.sample_unique_identifier === matchId
      || item.sample_identifier === matchId
    ));
    if (byUniqueId) return byUniqueId;
  }

  if (!issueSample.sample_id) return null;

  const bySampleId = samples.filter((item) => item.sample_id === issueSample.sample_id);
  if (!bySampleId.length) return null;
  if (bySampleId.length === 1) return bySampleId[0];

  const issueSampleType = issueSample.sample_type ?? "";
  const byIdAndType = bySampleId.find((item) => (item.sample_type ?? "") === issueSampleType);
  if (byIdAndType) return byIdAndType;

  return issueSampleType ? null : bySampleId[0];
};

export const findMatchingSubmissionSample = (issueSample, submission) => {
  const samples = submission?.samples_list ?? submission?.samples ?? [];
  return findIssueSampleInList(issueSample, samples);
};

/** Resolve test_list for resolvesampleissues from issue row, matched sample, or submission. */
export const resolveIssueSampleTests = (issueSample, submission, sampleDetails = null) => {
  const candidates = [
    sampleDetails,
    issueSample,
    findMatchingSubmissionSample(issueSample, submission),
  ];

  for (const sample of candidates) {
    if (!sample) continue;
    const tests = getIssueSampleTests(sample);
    if (tests.length) return tests;
  }

  if (getSamplesWithIssues(submission).length === 1) {
    const submissionTests = normalizeTestList(submission?.tests_list ?? submission?.test_list);
    if (submissionTests.length) return submissionTests;
  }

  return [];
};

export const buildIssueSampleRecord = (issueSample, submission) => ({
  sample_unique_identifier: getIssueSampleUniqueIdentifier(issueSample),
  sample_id: issueSample?.sample_id || "",
  sample_type: issueSample?.sample_type || "",
  sample_category: issueSample?.side || submission?.sample_category || "",
  issue_type: normalizeIssueType(issueSample?.issue_type),
});

export const buildEditableSampleFromIssue = (issueSample, submission) => {
  const record = buildIssueSampleRecord(issueSample, submission);
  return {
    sample_id: record.sample_id,
    sample_type: record.sample_type,
    sample_category: record.sample_category,
    sample_unique_identifier: record.sample_unique_identifier,
    tests_list: getIssueSampleTests(issueSample),
    issue_type: record.issue_type,
  };
};

export const mergeEditableIssueSample = (baseSample, detailSample) => {
  if (!detailSample) return baseSample;
  const mergedTests = getIssueSampleTests(detailSample).length
    ? getIssueSampleTests(detailSample)
    : getIssueSampleTests(baseSample);
  return {
    ...baseSample,
    ...detailSample,
    tests_list: mergedTests,
    issue_type: baseSample.issue_type || detailSample.issue_type,
  };
};

const RESOLVE_SAMPLE_OMIT_KEYS = new Set([
  "uuid",
  "tests_list",
  "test_list",
  "image_file",
  "image_src",
  "sample_category",
  "issue_type",
]);

const resolveSampleTypeValue = (...candidates) => {
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

/** Build sample payload for resolvesampleissues/ — mirrors submissiondraft/ sample fields with test_list nested. */
export const buildResolveSampleIssueSamplePayload = (
  issueSample,
  submission,
  sampleDetails = null,
) => {
  const record = buildIssueSampleRecord(issueSample, submission);
  const source = sampleDetails ?? issueSample ?? {};
  const testList = resolveIssueSampleTests(issueSample, submission, sampleDetails ?? source);

  const draftFields = {};
  Object.entries(source).forEach(([key, value]) => {
    if (RESOLVE_SAMPLE_OMIT_KEYS.has(key) || value === undefined) return;
    draftFields[key] = value;
  });

  const sampleType = resolveSampleTypeValue(
    source.sample_type,
    record.sample_type,
    issueSample?.sample_type,
  );

  return {
    ...draftFields,
    sample_unique_identifier: record.sample_unique_identifier,
    sample_id: source.sample_id || record.sample_id,
    sample_type: sampleType,
    side: issueSample?.side || source.side || record.sample_category || submission?.sample_category || "",
    issue_type: record.issue_type,
    test_list: testList,
  };
};

export const getSampleIssueColumnDisplay = (submission) => {
  if (!hasSampleIssues(submission)) return null;

  const issues = getSamplesWithIssues(submission);
  if (isExpandableSampleIssues(submission)) {
    return {
      type: "count",
      count: issues.length,
      label: `${issues.length} ${issues.length === 1 ? "issue" : "issues"}`,
    };
  }

  const issueType = normalizeIssueType(issues[0]?.issue_type);
  return {
    type: "single",
    issueType,
    label: getIssueDisplayLabel(issueType),
    showCreditsRefunded: true,
  };
};
